import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment"; // Make sure this file exports API_BASE_PATH

const PRODUCT_BASE_URL = `${API_BASE_PATH}/products/`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

export interface Product {
  product_id?: number;
  product_name: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  modules?: Array<{ module_name: string }>; // Ensure modules is an array of objects with module_name
}
export interface Module {
  module_id: number;
  module_name: string;
  description: string;
}

// ✅ Fetch all products
export const fetchProducts = async (_token: string | null): Promise<Product[]> => {
  try {
    const response = await axios.get(PRODUCT_BASE_URL, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    // Ensure `modules` is always an array
    const productsWithModules = response.data.map((product: Product) => ({
      ...product,
      modules: Array.isArray(product.modules) ? product.modules : [], // Ensure it's an array, fallback to empty array
    }));
    return productsWithModules;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✅ Create a new product
export const createProduct = async (_token: string | null, product: Product): Promise<Product> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", product.product_name);
    formData.append("description", product.description || "");

    const response = await axios.post(PRODUCT_BASE_URL, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        authorization: "Bearer " + _token,
      },
    });

    toast.success("Product created successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✅ Update an existing product
export const updateProduct = async (
  _token: string | null,
  productId: number | string,
  product: Product
): Promise<Product> => {
  try {
    const formData = new URLSearchParams();
    formData.append("product_name", product.product_name);
    formData.append("description", product.description || "");

    const response = await axios.put(
      `${PRODUCT_BASE_URL}/${productId}`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          authorization: "Bearer " + _token,
        },
      }
    );

    toast.success("Product updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✅ Delete a product
export const deleteProduct = async (
  _token: string | null,
  productId: number | string
): Promise<void> => {
  try {
    await axios.delete(`${PRODUCT_BASE_URL}/${productId}`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    toast.success("Product deleted successfully");
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✅ Client Interface (Separate and clean, no conflict with Product)
export interface Client {
  company_license: string;
  company_name: string;
  email: string;
  server_name: string;
  company_batch: string;
  contact_number: number;
  product_id: number;
  status: string;
  client_id: string;
  product: {
    product_id: number;
    product_name: string;
    description: string;
    created_at: string;
    updated_at: string;
  };
}

// ✅ Fetch all clients
export const fetchClients = async (_token: string | null): Promise<Client[]> => {
  try {
    const response = await axios.get(PRODUCT_BASE_URL, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};
